<script>
import { mapActions } from 'vuex'
import managementMixin from '@/modules/Management/managementMixin'

export default {
  name: 'ManagementUserProfiles',
  components: {
    ContentHeader: () => import('@/components/general/ContentHeader'),
    Action: () => import('@/components/general/Action'),
    FilterList: () => import('@/components/general/FilterList'),
    FilterListSearch: () => import('@/components/general/FilterListSearch'),
    Datatable: () => import('@/components/general/Datatable'),
    Pagination: () => import('@/components/general/Pagination'),
    Dropdown: () => import('@/components/general/Dropdown'),
    DropdownLink: () => import('@/components/general/DropdownLink'),
    Tabs: () => import('@/components/general/Tabs')
  },

  mixins: [ managementMixin ],

  data () {
    return {
      pagination: {
        query: {},
        filter: {},
        order: {
          active: 'DESC',
          created_at: 'DESC',
          name: 'ASC'
        },

        page: 1,
        limit: 12,
        lastPage: 3
      }
    }
  },

  computed: {
    canReadUsers () {
      return [
        'read',
        'write'
      ].indexOf(this.getContextPermission('users')) !== -1
    },

    canWrite () {
      return this.getContextPermission('profiles') === 'write'
    },

    profiles () {
      return this.$store.getters.profiles || []
    }
  },

  created () {
    if (this.$route.name !== 'management.user.profiles') {
      return
    }

    this.setup()
  },

  methods: {
    ...mapActions([
      'setFetching',
      'setFeedback',
      'attemptListProfiles',
      'attemptToggleProfile'
    ]),

    createNewProfile () {
      this.$router.push({ name: 'management.user.profiles.add' })
    },

    edit (id) {
      this.$router.push({
        name: 'management.user.profiles.edit',
        params: {
          id: id
        }
      })
    },

    toggle (id, status) {
      this.setFetching(true)

      this.attemptToggleProfile(id).then(() => {
        this.setFetching(false)

        if (status === true) {
          this.setFeedback({ message: this.$t('management.user.profile:feedback.toggle.success.inactive') })
        } else {
          this.setFeedback({ message: this.$t('management.user.profile:feedback.toggle.success.active') })
        }

        this.setup()
      })
        .catch(() => {
          this.setFeedback({ message: this.$t('management.user.profile:feedback.toggle.error') })
          this.setFetching(false)
        })
    },

    searchProfile (search) {
      this.pagination.query.name = search

      if (search === '') {
        this.pagination.query = {}
      }

      this.pagination.page = 1
      this.setup()
    },

    dateFormat (datetime) {
      if (datetime === '' || datetime === undefined || datetime === null) {
        return datetime
      }

      const date = new Date(Date.parse(datetime))

      return Intl.DateTimeFormat('pt-BR').format(date)
    },

    prevPage () {
      if (this.pagination.page > 1) {
        this.pagination.page -= 1
      }

      this.setup()
    },

    nextPage () {
      if (this.pagination.page < this.pagination.lastPage) {
        this.pagination.page += 1
      }

      this.setup()
    },

    firstPage () {
      this.pagination.page = 1
      this.setup()
    },

    lastPage () {
      this.pagination.page = this.pagination.lastPage
      this.setup()
    },

    setup () {
      this.setFetching(true)

      this.attemptListProfiles(this.pagination).then((data) => {
        this.pagination.page = data.actualPage
        this.pagination.lastPage = data.lastPage
      })
        .finally(() => {
          this.setFetching(false)
        })
    },

    viewUserProfileUsers (id) {
      this.$router.push({
        name: 'management.user.profiles.list.users',
        params: { id }
      })
    }
  }
}
</script>

<template>
  <div class="main-content">
    <ContentHeader
      :title="$t('management:header.title')"
      :description="$t('management:header.description')"
      background="/assets/images/themes/default/png/management_banner.png"
    >
      <Tabs
        slot="tabs"
        dark
        :links="availableTabLink"
      />
    </ContentHeader>
    <FilterList>
      <Action
        v-if="canWrite"
        slot="button"
        type="button"
        primary
        large
        fixed-width
        :text="$t('management.user.profiles:btn.add')"
        @click="createNewProfile"
      />

      <FilterListSearch
        slot="search"
        hide-error-details
        @search="searchProfile"
      />
    </FilterList>
    <div
      v-if="profiles.length > 0"
      class="center management-content"
    >
      <Datatable :items="profiles">
        <template slot="thead">
          <tr>
            <th class="th">
              {{ $t('management.user.profiles.list:datatable.header.1') }}
            </th>
            <th class="th">
              {{ $t('management.user.profiles.list:datatable.header.2') }}
            </th>
            <th class="th">
              {{ $t('management.user.profiles.list:datatable.header.3') }}
            </th>
            <th class="th">
              {{ $t('management.user.profiles.list:datatable.header.4') }}
            </th>
            <th
              class="th"
              width="40"
            />
          </tr>
        </template>
        <template
          slot="tbody"
          slot-scope="props"
        >
          <tr>
            <td class="td">
              <span class="td-text bolder">{{ props.item.name }}</span>
            </td>
            <td class="td">
              <span class="td-text">{{ dateFormat(props.item.createdAt) }}</span>
            </td>
            <td class="td">
              <span class="td-text">{{ props.item.totalLinks }}</span>
            </td>
            <td class="td">
              <span
                v-if="props.item.status"
                class="td-tag active-ribbon"
              >{{ $t('management.list:active') }}</span>
              <span
                v-else
                class="td-tag inactive-ribbon"
              >{{ $t('management.list:inactive') }}</span>
            </td>
            <td
              v-if="canWrite || canReadUsers"
              class="td td-actions"
              width="40"
            >
              <Dropdown
                icon="dots-vertical"
                right
              >
                <DropdownLink
                  v-if="canReadUsers"
                  :text="$t('management.user.profiles:users.list.view')"
                  @click="viewUserProfileUsers(props.item.id)"
                />
                <DropdownLink
                  v-if="canWrite"
                  :text="$t('global:edit')"
                  @click="edit(props.item.id)"
                />
                <DropdownLink
                  v-if="props.item.status && canWrite && props.item.totalLinks === 0"
                  :text="$t('global:deactivate')"
                  @click="toggle(props.item.id, props.item.status)"
                />
                <DropdownLink
                  v-else-if="!props.item.status && canWrite"
                  :text="$t('global:activate')"
                  @click="toggle(props.item.id, props.item.status)"
                />
              </Dropdown>
            </td>
          </tr>
        </template>
      </Datatable>
      <Pagination
        :active-page="pagination.page"
        :page-count="pagination.lastPage"
        @firstPage="firstPage"
        @lastPage="lastPage"
        @nextPage="nextPage"
        @previousPage="prevPage"
      />
    </div>

    <RouterView />
  </div>
</template>

<style src="@/assets/styles/themes/default/management.css"></style>
